import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Code2, PlayCircle, Zap, Bot, Chrome, Globe, LogIn,
  Users, FolderOpen, Calendar, BarChart3, Shield, MessageSquare,
  Wand2, CheckCircle, Check, X, ChevronDown, Crown, Rocket,
  Sun, Moon
} from "lucide-react";

// ── Contact-to-upgrade (WhatsApp) — replace number once Razorpay is live ──
const WHATSAPP_NUMBER = "918095526797";
const WA_PRO_MSG  = encodeURIComponent("Hi Ashish, I'd like to upgrade to AutoQA Pro (₹2,500/month). Please share payment details.");
const WA_TEAM_MSG = encodeURIComponent("Hi Ashish, I'd like to upgrade to AutoQA Team plan (₹6,500/month). Please share payment details.");

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, login, loading } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [openFaq, setOpenFaq] = useState(null);

  const pricingPlans = [
    {
      name: "Free",
      price: "₹0",
      period: "/month",
      tagline: "Get started, no credit card needed",
      color: "#00E599",
      cta: "Start Free",
      ctaAction: "login",
      highlighted: false,
      features: [
        { text: "50 test executions/month",       included: true },
        { text: "10 AI test generations/month",    included: true },
        { text: "1 user",                          included: true },
        { text: "Chromium browser only",           included: true },
        { text: "Basic analytics",                 included: true },
        { text: "Visual regression testing",       included: false },
        { text: "Firefox & WebKit browsers",       included: false },
        { text: "Slack / Discord notifications",   included: false },
        { text: "Team collaboration",              included: false },
        { text: "Priority support",                included: false },
      ],
    },
    {
      name: "Pro",
      price: "₹2,500",
      period: "/month",
      tagline: "For individual developers & freelancers",
      color: "#D946EF",
      cta: "Contact to Upgrade →",
      ctaAction: "whatsapp_pro",
      highlighted: false,
      features: [
        { text: "500 test executions/month",       included: true },
        { text: "100 AI test generations/month",   included: true },
        { text: "1 user — all features unlocked",  included: true },
        { text: "Chromium, Firefox & WebKit",      included: true },
        { text: "Advanced analytics",              included: true },
        { text: "Visual regression testing",       included: true },
        { text: "Slack / Discord notifications",   included: true },
        { text: "Team collaboration",              included: false },
        { text: "Priority support",                included: false },
        { text: "Custom integrations",             included: false },
      ],
    },
    {
      name: "Team",
      price: "₹6,500",
      period: "/month",
      tagline: "Best for growing QA teams",
      color: "#00E599",
      cta: "Contact to Upgrade →",
      ctaAction: "whatsapp_team",
      highlighted: true, // Most Popular
      badge: "Most Popular",
      features: [
        { text: "2,000 test executions/month",     included: true },
        { text: "Unlimited AI test generations",   included: true },
        { text: "Up to 5 seats",                   included: true },
        { text: "All browsers",                    included: true },
        { text: "Advanced analytics",              included: true },
        { text: "Visual regression testing",       included: true },
        { text: "Slack / Discord notifications",   included: true },
        { text: "Team collaboration & roles",      included: true },
        { text: "Priority support",                included: true },
        { text: "Custom integrations",             included: false },
      ],
    },
    {
      name: "Business",
      price: "₹16,000",
      period: "/month",
      tagline: "For enterprise QA operations",
      color: "#F59E0B",
      cta: "Contact Us",
      ctaAction: "contact",
      highlighted: false,
      features: [
        { text: "Unlimited test executions",       included: true },
        { text: "Unlimited AI test generations",   included: true },
        { text: "Up to 20 seats",                  included: true },
        { text: "All browsers",                    included: true },
        { text: "Advanced analytics",              included: true },
        { text: "Visual regression testing",       included: true },
        { text: "Slack / Discord notifications",   included: true },
        { text: "Team collaboration & roles",      included: true },
        { text: "Dedicated support",               included: true },
        { text: "Custom integrations",             included: true },
      ],
    },
  ];

  const faqItems = [
    {
      q: "Is there a free trial on paid plans?",
      a: "Yes — every paid plan comes with a 14-day free trial. You can upgrade, explore all features, and cancel before the trial ends with no charge.",
    },
    {
      q: "Can I cancel anytime?",
      a: "Absolutely. No contracts, no lock-in. Cancel from your account settings at any time and you keep access until the end of your billing period.",
    },
    {
      q: "Which browsers does AutoQA support?",
      a: "Pro, Team, and Business plans support Chromium, Firefox (Gecko), and WebKit (Safari engine). The Free plan runs on Chromium only.",
    },
    {
      q: "Do I need to know how to code?",
      a: "Not at all. You describe your test in plain English and AutoQA generates and runs it for you. No Playwright or Selenium knowledge required.",
    },
    {
      q: "How does self-healing work?",
      a: "When a selector breaks (e.g., a CSS class was renamed), AutoQA uses Gemini AI to scan the live page HTML and find the correct element automatically. The healed selector is saved for future runs.",
    },
    {
      q: "What happens when I hit my execution limit?",
      a: "You'll get an email notification at 80% usage. Once you hit the limit, new executions are queued until the next billing cycle — or you can upgrade your plan at any time.",
    },
  ];

  const handlePricingCta = (action) => {
    if (action === "login") {
      login();
    } else if (action === "whatsapp_pro") {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${WA_PRO_MSG}`, "_blank");
    } else if (action === "whatsapp_team") {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${WA_TEAM_MSG}`, "_blank");
    } else if (action === "contact") {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Ashish, I'm interested in AutoQA Business plan. Please share details.")}`, "_blank");
    }
  };

  const features = [
    {
      icon: <Wand2 className="w-6 h-6 text-[#00E599]" />,
      title: "AI Test Generation",
      description: "Generate test cases from user stories, meeting notes, or Figma screenshots using Gemini 3 Pro.",
      color: "00E599"
    },
    {
      icon: <Code2 className="w-6 h-6 text-[#D946EF]" />,
      title: "Multi-Language Code",
      description: "Get test scripts in Python, JavaScript, and Java. Edit, save, and reuse your generated code.",
      color: "D946EF"
    },
    {
      icon: <Zap className="w-6 h-6 text-[#06B6D4]" />,
      title: "Self-Healing Tests",
      description: "AI automatically fixes broken selectors and adapts to UI changes in real-time.",
      color: "06B6D4"
    },
    {
      icon: <FolderOpen className="w-6 h-6 text-[#F59E0B]" />,
      title: "Test Suites",
      description: "Organize tests into collections. Run multiple tests together with a single click.",
      color: "F59E0B"
    },
    {
      icon: <Calendar className="w-6 h-6 text-[#8B5CF6]" />,
      title: "Scheduled Runs",
      description: "Automate test execution with daily, weekly, or custom schedules.",
      color: "8B5CF6"
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-[#EC4899]" />,
      title: "Analytics Dashboard",
      description: "Track pass rates, execution trends, and identify flaky tests with detailed insights.",
      color: "EC4899"
    },
    {
      icon: <Users className="w-6 h-6 text-[#10B981]" />,
      title: "Team Collaboration",
      description: "Create teams, invite members, and share test cases with role-based access control.",
      color: "10B981"
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-[#3B82F6]" />,
      title: "Comments & Notes",
      description: "Add comments to test executions for better team communication and documentation.",
      color: "3B82F6"
    },
    {
      icon: <Shield className="w-6 h-6 text-[#EF4444]" />,
      title: "Secure & Private",
      description: "Your API keys are encrypted. Multi-tenant architecture keeps your data isolated.",
      color: "EF4444"
    }
  ];

  const d = isDark; // shorthand for ternary classes

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-300 ${d ? "bg-[#030712] text-white" : "bg-white text-slate-900"}`}>
      {/* Background Effects — dark only */}
      {d && (
        <>
          <div className="fixed inset-0 hero-glow" />
          <div className="fixed inset-0 opacity-5"
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"-%3E%3Cg fill="none" fill-rule="evenodd"-%3E%3Cg fill="%23ffffff" fill-opacity="0.4"-%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            }}
          />
        </>
      )}

      {/* Navigation */}
      <nav className={`relative z-10 px-6 md:px-12 py-5 border-b ${d ? "border-white/5" : "border-slate-100"}`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#00b37a] rounded-lg flex items-center justify-center shadow-sm">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <h1 className={`text-xl font-bold ${d ? "text-white" : "text-slate-900"}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>AutoQA.ai</h1>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Section links — desktop only */}
            <div className="hidden md:flex items-center gap-5 mr-1">
              <button
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className={`text-sm transition-colors ${d ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}
              >
                Pricing
              </button>
              <button
                onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })}
                className={`text-sm transition-colors ${d ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}
              >
                FAQ
              </button>
            </div>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${d ? "text-slate-400 hover:text-white hover:bg-white/10" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}
              aria-label={d ? "Switch to light mode" : "Switch to dark mode"}
            >
              {d ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
            </button>

            {!user ? (
              <Button
                data-testid="nav-login-btn"
                className="rounded-lg font-semibold bg-[#00b37a] text-white hover:bg-[#009966] transition-all shadow-sm"
                onClick={login}
                disabled={loading}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            ) : (
              <Button
                data-testid="nav-dashboard-btn"
                className="rounded-lg font-semibold bg-[#00b37a] text-white hover:bg-[#009966] transition-all shadow-sm"
                onClick={() => navigate('/dashboard')}
              >
                Dashboard
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E599]/10 border border-[#00E599]/20 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-[#00E599]" />
              <span className="text-sm text-[#00E599] font-semibold">Powered by Gemini 3 Pro</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter leading-[1.05] mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Enterprise Test Automation,<br />Engineered with AI
            </h1>

            <p className={`text-base md:text-lg max-w-2xl mx-auto mb-12 leading-relaxed ${d ? "text-slate-400" : "text-slate-500"}`}>
              AutoQA converts your requirements into production-ready browser tests, executes them
              across every major engine, and repairs them automatically as your product evolves —
              so your team ships with confidence, not caveats.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
              {!user ? (
                <Button
                  data-testid="hero-login-btn"
                  size="lg"
                  className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-xl font-semibold text-base px-8 neon-glow active:scale-95 shadow-lg shadow-[#00b37a]/20"
                  onClick={login}
                  disabled={loading}
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Get Started with Google
                </Button>
              ) : (
                <Button
                  data-testid="hero-dashboard-btn"
                  size="lg"
                  className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-xl font-semibold text-base px-8 neon-glow active:scale-95 shadow-lg shadow-[#00b37a]/20"
                  onClick={() => navigate('/dashboard')}
                >
                  <PlayCircle className="w-5 h-5 mr-2" />
                  Open Dashboard
                </Button>
              )}
              <Button
                data-testid="hero-pricing-btn"
                size="lg"
                variant="ghost"
                className={`rounded-xl font-semibold text-base px-8 border transition-colors ${d ? "text-slate-300 hover:text-white hover:bg-white/5 border-white/10" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-slate-200"}`}
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Pricing
              </Button>
            </div>

            {/* Trust Badges */}
            <div className={`flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm ${d ? "text-slate-500" : "text-slate-400"}`}>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00E599]" />
                <span>Free tier — no credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <Rocket className="w-4 h-4 text-[#00E599]" />
                <span>50 executions included each month</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#00E599]" />
                <span>Encrypted credentials, isolated tenancy</span>
              </div>
            </div>
          </motion.div>

          {/* ── Product Preview ── */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-20"
          >
            <div className="relative max-w-5xl mx-auto">
              {/* Ambient glow behind the frame */}
              <div className="absolute -inset-8 bg-[#00E599]/10 blur-[80px] rounded-full pointer-events-none" />

              {/* Browser chrome frame */}
              <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl"
                   style={{ background: 'rgba(11,15,26,0.95)', backdropFilter: 'blur(20px)' }}>
                {/* Title bar */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/[0.02]">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                    <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                    <span className="w-3 h-3 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="flex items-center gap-2 px-4 py-1 rounded-md bg-black/40 border border-white/5">
                      <Shield className="w-3 h-3 text-[#00E599]" />
                      <span className="text-[11px] text-slate-400 font-mono">autoqa.tech/dashboard</span>
                    </div>
                  </div>
                </div>

                {/* Mock dashboard body */}
                <div className="p-5 md:p-6">
                  {/* Stat row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    {[
                      { label: 'Total Tests',   value: '248',   color: '#00E599' },
                      { label: 'Pass Rate',     value: '96.4%', color: '#00E599' },
                      { label: 'Executions',    value: '1,392', color: '#D946EF' },
                      { label: 'Auto-Healed',   value: '37',    color: '#06B6D4' },
                    ].map(s => (
                      <div key={s.label} className="rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3 text-left">
                        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{s.label}</p>
                        <p className="text-xl font-bold" style={{ color: s.color, fontFamily: 'Unbounded, sans-serif' }}>
                          {s.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Test list */}
                    <div className="lg:col-span-2 rounded-lg border border-white/5 bg-white/[0.02] p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-300">Recent Executions</p>
                        <span className="text-[10px] text-slate-600">Last 24 hours</span>
                      </div>
                      <div className="space-y-2">
                        {[
                          { name: 'Checkout — card payment flow',   status: 'Passed', dur: '12.4s', ok: true  },
                          { name: 'Login — Google OAuth redirect',   status: 'Passed', dur: '4.1s',  ok: true  },
                          { name: 'Search — filter by category',     status: 'Healed', dur: '8.9s',  ok: true, healed: true },
                          { name: 'Profile — avatar upload',         status: 'Failed', dur: '31.0s', ok: false },
                          { name: 'API — POST /orders contract',     status: 'Passed', dur: '0.8s',  ok: true  },
                        ].map(t => (
                          <div key={t.name} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-black/30 border border-white/5">
                            <div className="flex items-center gap-2.5 min-w-0">
                              {t.ok
                                ? <CheckCircle className="w-3.5 h-3.5 text-[#00E599] shrink-0" />
                                : <X className="w-3.5 h-3.5 text-[#EF4444] shrink-0" />}
                              <span className="text-[11px] text-slate-300 truncate">{t.name}</span>
                              {t.healed && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#06B6D4]/15 text-[#06B6D4] font-semibold shrink-0">
                                  AUTO-HEALED
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="text-[10px] text-slate-600 font-mono">{t.dur}</span>
                              <span className={`text-[10px] font-semibold ${t.ok ? 'text-[#00E599]' : 'text-[#EF4444]'}`}>
                                {t.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pass-rate trend */}
                    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
                      <p className="text-xs font-semibold text-slate-300 mb-3">Pass Rate Trend</p>
                      <div className="flex items-end justify-between gap-1.5 h-28">
                        {[62, 71, 68, 79, 84, 81, 90, 88, 94, 96].map((h, i) => (
                          <div key={i} className="flex-1 rounded-t-sm"
                               style={{
                                 height: `${h}%`,
                                 background: `linear-gradient(180deg, #00E599 0%, rgba(0,229,153,0.15) 100%)`,
                               }} />
                        ))}
                      </div>
                      <div className="flex justify-between mt-2 text-[9px] text-slate-600">
                        <span>10 days ago</span>
                        <span>Today</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-slate-600 mt-5">
              Live execution results, self-healing status, and trend analytics — in a single view.
            </p>
          </motion.div>

          {/* Browser Support */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-24"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 text-center mb-8">Supports All Major Browsers</p>
            <div className="flex justify-center gap-8 flex-wrap">
              <div className="flex flex-col items-center gap-2">
                <Chrome className="w-12 h-12 text-slate-400" />
                <span className="text-sm text-slate-500">Chromium</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Globe className="w-12 h-12 text-slate-400" />
                <span className="text-sm text-slate-500">Firefox</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Globe className="w-12 h-12 text-slate-400" />
                <span className="text-sm text-slate-500">WebKit</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className={`relative z-10 px-6 md:px-12 py-24 ${d ? "bg-[#0B0F1A]/50" : "bg-slate-50"}`}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20 mb-4">
              Features
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              A Complete Quality Engineering Platform
            </h2>
            <p className={`max-w-2xl mx-auto ${d ? "text-slate-400" : "text-slate-500"}`}>
              Purpose-built capabilities spanning test authoring, execution, analysis, and team
              governance — consolidated into a single system of record.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
              >
                <Card className={`rounded-xl h-full transition-all duration-300 ${d ? "glass-card border-white/5 hover:border-white/10" : "bg-white border border-slate-200 shadow-sm hover:shadow-md"}`}>
                  <CardContent className="p-6">
                    <div 
                      className="w-12 h-12 rounded-sm flex items-center justify-center mb-4"
                      style={{ backgroundColor: `#${feature.color}15` }}
                    >
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      {feature.title}
                    </h3>
                    <p className={`text-sm leading-relaxed ${d ? "text-slate-400" : "text-slate-500"}`}>{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative z-10 px-6 md:px-12 py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#00b37a]/10 text-[#00b37a] border-[#00b37a]/20 mb-4">
              How It Works
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              From Requirement to Report in Three Steps
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#00E599]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-[#00E599]">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Describe Your Test
              </h3>
              <p className="text-slate-400">
                Write test steps in plain English or upload user stories and Figma designs.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#D946EF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-[#D946EF]">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                AI Generates Code
              </h3>
              <p className="text-slate-400">
                Our AI creates executable Playwright tests in Python, JavaScript, or Java.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#06B6D4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-[#06B6D4]">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Execute & Report
              </h3>
              <p className="text-slate-400">
                Run tests across browsers with screenshots, logs, and detailed reports.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 px-6 md:px-12 py-24 bg-[#0B0F1A]/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="text-4xl md:text-5xl font-bold text-[#00E599] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                3
              </p>
              <p className="text-slate-400">Languages Supported</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <p className="text-4xl md:text-5xl font-bold text-[#D946EF] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                3
              </p>
              <p className="text-slate-400">Browser Engines</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <p className="text-4xl md:text-5xl font-bold text-[#06B6D4] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                100%
              </p>
              <p className="text-slate-400">Cloud Native</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <p className="text-4xl md:text-5xl font-bold text-[#F59E0B] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                24/7
              </p>
              <p className="text-slate-400">Scheduled Runs</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Pricing Section ── */}
      <section id="pricing" className={`relative z-10 px-6 md:px-12 py-24 ${d ? "" : "bg-white"}`}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#00E599]/10 text-[#00E599] border-[#00E599]/20 mb-4">
              Pricing
            </Badge>
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${d ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Unbounded, sans-serif" }}>
              Transparent Pricing That Scales With You
            </h2>
            <p className={`max-w-2xl mx-auto ${d ? "text-slate-400" : "text-slate-500"}`}>
              Begin at no cost and expand as your coverage grows. No contracts, no hidden fees,
              and a 14-day evaluation period on every paid tier.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
            {pricingPlans.map((plan, idx) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="flex"
              >
                <div
                  className={`relative flex flex-col w-full rounded-xl border transition-all duration-300 p-6
                    ${plan.highlighted
                      ? "border-[#00b37a]/50 shadow-[0_0_32px_rgba(0,179,122,0.15)]"
                      : d ? "border-white/5 hover:border-white/10" : "border-slate-200 hover:border-slate-300 hover:shadow-md"
                    }`}
                  style={d ? { background: "rgba(11,15,26,0.7)", backdropFilter: "blur(20px)" } : { background: "#ffffff" }}
                >
                  {/* Most Popular badge */}
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-[#00b37a] text-white">
                        <Crown className="w-3 h-3" />
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  {/* Plan header */}
                  <div className="mb-6">
                    <p className="text-sm font-bold uppercase tracking-widest mb-1" style={{ color: plan.color }}>
                      {plan.name}
                    </p>
                    <div className="flex items-end gap-1 mb-2">
                      <span className={`text-4xl font-extrabold ${d ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Unbounded, sans-serif" }}>
                        {plan.price}
                      </span>
                      <span className={`mb-1 ${d ? "text-slate-400" : "text-slate-500"}`}>{plan.period}</span>
                    </div>
                    <p className={`text-sm ${d ? "text-slate-400" : "text-slate-500"}`}>{plan.tagline}</p>
                  </div>

                  {/* CTA */}
                  <Button
                    className={`w-full rounded-lg font-semibold mb-6 ${
                      plan.highlighted
                        ? "bg-[#00b37a] text-white hover:bg-[#009966]"
                        : d ? "bg-white/5 text-white hover:bg-white/10 border border-white/10" : "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200"
                    }`}
                    onClick={() => handlePricingCta(plan.ctaAction)}
                  >
                    {plan.cta}
                  </Button>

                  {/* Divider */}
                  <div className={`border-t mb-6 ${d ? "border-white/5" : "border-slate-100"}`} />

                  {/* Feature list */}
                  <ul className="space-y-3 flex-1">
                    {plan.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        {f.included
                          ? <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#00b37a]" />
                          : <X    className={`w-4 h-4 mt-0.5 shrink-0 ${d ? "text-slate-600" : "text-slate-300"}`} />
                        }
                        <span className={f.included ? (d ? "text-slate-300" : "text-slate-700") : (d ? "text-slate-600" : "text-slate-400")}>
                          {f.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ Section ── */}
      <section id="faq" className={`relative z-10 px-6 md:px-12 py-24 ${d ? "bg-[#0B0F1A]/50" : "bg-slate-50"}`}>
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <Badge className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20 mb-4">
              FAQ
            </Badge>
            <h2 className={`text-3xl md:text-4xl font-bold ${d ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Unbounded, sans-serif" }}>
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqItems.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.06 }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className={`w-full text-left flex items-center justify-between gap-4 p-5 rounded-xl border transition-all duration-200 ${d ? "border-white/5 hover:border-white/10" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  style={d ? { background: "rgba(11,15,26,0.7)", backdropFilter: "blur(20px)" } : {}}
                >
                  <span className={`font-semibold text-sm md:text-base ${d ? "text-white" : "text-slate-900"}`}>{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 shrink-0 transition-transform duration-200 ${openFaq === idx ? "rotate-180" : ""} ${d ? "text-slate-400" : "text-slate-500"}`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div
                        className={`px-5 pt-3 pb-5 text-sm leading-relaxed border border-t-0 rounded-b-xl -mt-1 ${d ? "text-slate-400 border-white/5" : "text-slate-600 border-slate-200 bg-white"}`}
                        style={d ? { background: "rgba(11,15,26,0.5)" } : {}}
                      >
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 md:px-12 py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className={`p-12 rounded-xl text-center border ${d ? "glass-card border-[#00b37a]/20" : "bg-white border-slate-200 shadow-sm"}`}
          >
            <div className="w-16 h-16 bg-[#00b37a]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Bot className="w-8 h-8 text-[#00b37a]" />
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${d ? "text-white" : "text-slate-900"}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Ready to Elevate Your QA Process?
            </h2>
            <p className={`mb-8 max-w-xl mx-auto ${d ? "text-slate-400" : "text-slate-500"}`}>
              Teams rely on AutoQA to release faster without compromising quality.
              Start on the free tier today — no credit card, no commitment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {!user ? (
                <Button
                  data-testid="cta-login-btn"
                  size="lg"
                  className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-xl font-semibold text-base px-8 neon-glow"
                  onClick={login}
                  disabled={loading}
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Get Started with Google
                </Button>
              ) : (
                <Button
                  data-testid="cta-dashboard-btn"
                  size="lg"
                  className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-xl font-semibold text-base px-8 neon-glow"
                  onClick={() => navigate('/dashboard')}
                >
                  Open Dashboard
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`relative z-10 px-6 md:px-12 py-12 border-t ${d ? "border-white/5" : "border-slate-200"}`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#00b37a] rounded-sm flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <span className={`font-bold ${d ? "text-white" : "text-slate-900"}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>AutoQA.ai</span>
            </div>
            <p className={`text-sm ${d ? "text-slate-500" : "text-slate-400"}`}>
              &copy; 2026 AutoQA.ai - AI-Powered Test Automation Platform
            </p>
            <div className={`flex gap-4 text-sm ${d ? "text-slate-500" : "text-slate-400"}`}>
              <a href="#" className={`transition-colors ${d ? "hover:text-slate-300" : "hover:text-slate-700"}`}>Privacy Policy</a>
              <span>&middot;</span>
              <a href="#" className={`transition-colors ${d ? "hover:text-slate-300" : "hover:text-slate-700"}`}>Terms of Service</a>
              <span>&middot;</span>
              <a href="#" className={`transition-colors ${d ? "hover:text-slate-300" : "hover:text-slate-700"}`}>Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
